import { Color4, Color3, Vector3 } from '@babylonjs/core/Maths/math';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { EasingFunction, BezierCurveEase } from '@babylonjs/core/Animations/easing';
import { SphereBuilder } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import { SphereParticleEmitter } from '@babylonjs/core/Particles/EmitterTypes/sphereParticleEmitter';
import find from 'lodash/find';

import { MeshSystem } from '../System/meshSystem';
import { Animation } from '../System/animation';
import { MovingEntity } from '../Entity/movingEntity';
import { Planet } from './planet';
import { PlanetMap } from '../Map/planetMap';
// import { NoiseShader } from '../Shader/noise';

// https://www.youtube.com/watch?v=i4RtO_qIQHk

export const starMapDistance = 150;
export const minSize = 0.6;
export const maxSize = 4;
export const startSize = 1;

export interface StarCategory {
    name: string;
    temperature: number;
    color: Color3;
    planets: number;
    gravity: number;
    velocity: number;
}

export const StarCategories: StarCategory[] = [
    {
        name: 'Red Dwarf',
        temperature: 3000,
        color: new Color3(1, 0.4, 0.4),
        planets: 2,
        gravity: 1.2,
        velocity: 1
    },
    {
        name: 'Yellow Dwarf',
        temperature: 5000,
        color: new Color3(0.6, 0.5, 0),
        planets: 3,
        gravity: 1.1,
        velocity: 1.1
    },
    {
        name: 'White Dwarf',
        temperature: 12000,
        color: new Color3(0.5, 0.5, 0.5),
        planets: 4,
        gravity: 1,
        velocity: 0.9
    },
    {
        name: 'Blue Dwarf',
        temperature: 30000,
        color: new Color3(0.4, 0.4, 1),
        planets: 5,
        gravity: 0.7,
        velocity: 1.2
    },
];

export interface StarInterface {
    temperature: number,
    maxPlanet: number,
    texture?: string,
    number?: number,
    life?: number,
    power?: number,
}

export class Star extends MovingEntity {

    private shineAnimation: Animation;

    private curve: EasingFunction;

    public color: Color4;

    // rotateProgress = 0;

    public cycleSize = 0;

    protected planetMap: PlanetMap;

    public accelerating = false;

    constructor(system: MeshSystem, planetMap: PlanetMap, options: StarInterface) {
        super('star', system);
        this.planetMap = planetMap;
        this.shineAnimation = new Animation(this.system.animationManager);
        this.temperature = options.temperature;
        this.curve = new BezierCurveEase(.76, .01, .51, 1.33);
        // this.curve.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);

        setInterval(() => {
            if (this.direction.x !== 0 && this.trailParticles) {
                //  - Math.PI/4 because Isometric
                var angle = Math.atan(this.direction.y/this.direction.x) - Math.PI/4;   //radians
                this.trailParticles.minInitialRotation = angle;
                this.trailParticles.maxInitialRotation = angle;
            }
        }, 100)
    }
    
    protected cycleAbsorb = 1;

    private coreSphere: Mesh;

    // private coreMaterial: NoiseShader;

    private coreMaterial: StandardMaterial;

    protected surfaceParticles: ParticleSystem;

    protected coronaParticles: ParticleSystem;

    protected trailParticles: ParticleSystem;

    private resetParticles(color: Color3) {
        if (this.surfaceParticles) this.surfaceParticles.dispose();
        if (this.coronaParticles) this.coronaParticles.dispose();
        if (this.trailParticles) this.trailParticles.dispose();

        // Create a particle system
        this.surfaceParticles = new ParticleSystem("surfaceParticles", 100, this.system.scene);
        this.coronaParticles = new ParticleSystem("coronaParticles", 100, this.system.scene);
        this.trailParticles = new ParticleSystem("trailParticles", 50, this.system.scene);

        // Texture of each particle
        this.surfaceParticles.particleTexture = this.system.surfaceTexture.clone();
        this.coronaParticles.particleTexture = this.system.sparkleTexture.clone();
        this.trailParticles.particleTexture = this.system.sparkleTexture.clone();

        this.surfaceParticles.preWarmStepOffset = 10;
        this.surfaceParticles.preWarmCycles = 100;

        this.coronaParticles.preWarmStepOffset = 10;
        this.coronaParticles.preWarmCycles = 100;

        // Initial rotation
        this.surfaceParticles.minInitialRotation = -2 * Math.PI;
        this.surfaceParticles.maxInitialRotation = 2 * Math.PI;

        this.coronaParticles.minInitialRotation = -2 * Math.PI;
        this.coronaParticles.maxInitialRotation = 2 * Math.PI;

        // Where the sun particles come from
        var sunEmitter = new SphereParticleEmitter();
        sunEmitter.radius = 1;
        sunEmitter.radiusRange = 0; // emit only from shape surface

        // Assign particles to emitters
        this.surfaceParticles.emitter = this.coreSphere; // the starting object, the emitter
        this.surfaceParticles.particleEmitterType = sunEmitter;

        this.coronaParticles.emitter = this.coreSphere; // the starting object, the emitter
        this.coronaParticles.particleEmitterType = sunEmitter;

        this.trailParticles.emitter = this.coreSphere; // the starting object, the emitter
        // this.trailParticles.particleEmitterType = sunEmitter;

        // Life time of each particle (random between...
        this.surfaceParticles.minLifeTime = 0.5;
        this.surfaceParticles.maxLifeTime = 1.0;

        this.coronaParticles.minLifeTime = 0.6;
        this.coronaParticles.maxLifeTime= 1.2;

        this.trailParticles.minLifeTime = 0.4;
        this.trailParticles.maxLifeTime= 1.0;

        // Emission rate
        this.surfaceParticles.emitRate = 100;
        this.coronaParticles.emitRate = 100;
        this.trailParticles.emitRate = 50;

        // Blend mode : BLENDMODE_ONEONE, BLENDMODE_STANDARD, or BLENDMODE_ADD
        this.surfaceParticles.blendMode = ParticleSystem.BLENDMODE_ADD;
        this.coronaParticles.blendMode = ParticleSystem.BLENDMODE_ADD;
        this.trailParticles.blendMode = ParticleSystem.BLENDMODE_ADD;

        // Set the gravity of all particles
        this.surfaceParticles.gravity = new Vector3(0, 0, 0);
        this.coronaParticles.gravity = new Vector3(0, 0, 0);

        // Angular speed, in radians
        this.surfaceParticles.minAngularSpeed = -0.4;
        this.surfaceParticles.maxAngularSpeed = 0.4;

        this.coronaParticles.minAngularSpeed = -0.4;
        this.coronaParticles.maxAngularSpeed = 0.4;

        // Speed
        this.surfaceParticles.minEmitPower = 0;
        this.surfaceParticles.maxEmitPower = 0;
        this.surfaceParticles.updateSpeed = 0.005;

        this.coronaParticles.minEmitPower = 0
        this.coronaParticles.maxEmitPower = 0;

        // No billboard
        this.surfaceParticles.isBillboardBased = false;
        this.coronaParticles.isBillboardBased = true;
        this.trailParticles.isBillboardBased = true;

        // Render Order
        this.surfaceParticles.renderingGroupId = 3;
        this.coronaParticles.renderingGroupId = 1;
        this.trailParticles.renderingGroupId = 1;

        this.surfaceParticles.addColorGradient(0, new Color4(0.5, 0.5, 0.5, 0.0));
        this.surfaceParticles.addColorGradient(0.4, color.toColor4(1.0));
        this.surfaceParticles.addColorGradient(1.0, new Color4(0.5, 0.5, 0.5, 0.0));

        this.coronaParticles.addColorGradient(0, new Color4(0.1, 0.1, 0.1, 0.0));
        this.coronaParticles.addColorGradient(0.4, color.toColor4(1));
        this.coronaParticles.addColorGradient(1.0, new Color4(0.1, 0.1, 0.1, 0.0));

        this.trailParticles.addColorGradient(0, color.toColor4(1));
        this.trailParticles.addColorGradient(1.0, color.toColor4(0));

        this.trailParticles.addSizeGradient(0, 0); //size at start of particle lifetime
        this.trailParticles.addSizeGradient(0.1, 1); //size at end of particle lifetime

        let parentPos = this.transformMesh.position;
        let parentPrevius = parentPos.clone();
        let parentChange = Vector3.Zero();
        const surfaceOldUpdate = this.surfaceParticles.updateFunction;
        this.surfaceParticles.updateFunction = function(particles) {
            surfaceOldUpdate(particles);
            parentChange = parentPos.subtract(parentPrevius);
            parentPrevius = parentPos.clone();
            for (var index = 0; index < particles.length; index++) {
                var particle = particles[index];
                particle.position.addInPlace(parentChange);
            } 
        }

        const coronaOldUpdate = this.coronaParticles.updateFunction;
        this.coronaParticles.updateFunction = function(particles) {
            coronaOldUpdate(particles);
            for (var index = 0; index < particles.length; index++) {
                var particle = particles[index];
                particle.position.addInPlace(parentChange);
            }
        }

        // Start the particle system
        this.surfaceParticles.start();
        this.coronaParticles.start();
        this.trailParticles.start();
    }

    protected createStar() {
        if (this.coreSphere && this.coreSphere.isVisible) return;
        // Create core sphere
        this.coreSphere = SphereBuilder.CreateSphere("coreSphere", {diameter: 2, segments: 16}, this.system.scene);
        this.coreSphere.parent = this.transformMesh;
        this.coreSphere.renderingGroupId = 3;
        // this.coreSphere.rotation.y = Math.PI;
        // this.coreSphere.rotation.z = -Math.PI/3;
        // Create core material
        this.coreMaterial = new StandardMaterial('', this.system.scene);
        // this.coreMaterial = new NoiseShader(this.system.scene);
        this.coreSphere.material = this.coreMaterial;

        this.setTemperature(this.temperature);
        this.setSize(this.size);
        this.system.checkActiveMeshes();
    }

   public maxPlanet: number;

    protected setMaxPlanet(maxPlanet: number) {
        this.maxPlanet = maxPlanet;
    }

    private temperature: number;

    protected setTemperature(temperature: number) {
        this.temperature = temperature;
        const starCat = find(StarCategories, (s) => s.temperature === temperature);
        const {color} = starCat;
        this.color = color.toColor4();
        if (this.isStarVisible) {
            
            this.coreMaterial.emissiveColor = color;
            // const hsv = color.toHSV();
            // this.coreMaterial.colors = {color1: [hsv.r/360, hsv.g, 0.6], color2: [hsv.r/360, hsv.g, 0.95]}

            this.resetParticles(color);
            this.setSize(this.size)
        }
    }

    public setSize(size: number) {
        this._setSize(size);
        if (!this.isStarVisible) return;
        this.cycleSize = 1 / Math.sqrt(this.size);
        this.setHeartScale(size * 2);
    }

    protected setHeartScale(scale: number) {
        this.coreSphere.scaling.x = scale;
        this.coreSphere.scaling.y = scale;
        this.coreSphere.scaling.z = scale;

        this.surfaceParticles.minSize = scale * 1.0;
        this.surfaceParticles.maxSize = scale * 1.5;

        this.coronaParticles.minScaleX = scale * 0.5;
        this.coronaParticles.minScaleY = scale * 0.75;
        this.coronaParticles.maxScaleX = scale * 1.2;
        this.coronaParticles.maxScaleY = scale * 3.0;

        const trailScale = Math.sqrt(scale * 5);
        this.trailParticles.minScaleX = trailScale * 0.5;
        this.trailParticles.minScaleY = trailScale * 0.75;
        this.trailParticles.maxScaleX = trailScale * 1.2;
        this.trailParticles.maxScaleY = trailScale * 3.0;

        this.trailParticles.minLifeTime = Math.min(this.size * 0.2, 1);
        this.trailParticles.maxLifeTime= Math.min(this.size * 0.4, 1);
    }

    protected updateSize(size: number, time?:number, callback?: Function) {
        if (!this.isStarVisible) return;
        let currentsize = this.size;
        let change = size - currentsize;
        let animTime = (time)? time : 20;
        
        this.shineAnimation.simple(animTime, (count, perc) => {
            let newOpacity = 0.6 - Math.pow(perc, 4);
            this.setOpacity(newOpacity);
            let newSize = currentsize + this.curve.ease(perc) * change;
            this.setSize(newSize);
        }, () => {
            this.setSize(size);
            if (callback) callback();
        });
    }
    
    protected _disposeStar() {
        if (!this.coreSphere) return;
        this.coreSphere.isVisible = false;
        this.coreSphere.dispose();
        this.coreMaterial.dispose();
        this.coronaParticles.dispose();
        this.surfaceParticles.dispose();
        this.system.checkActiveMeshes();
    }

    protected setOpacity(opacity: number) {
        this.coreSphere.visibility = opacity;
    }

    private visibleDistance = 100; // ia Maximum Distance

    public isStarOnScreen(): boolean {
        let vd = this.visibleDistance;
        let p = this.position;
        let c = this.system.center;
        return (p.x < c.x + vd && p.x > c.x - vd && p.y < c.y + vd && p.y > c.y - vd);
    }

    private mapDistance = starMapDistance; // ia Maximum Distance
    
    public isStarOnMap(): boolean {
        let vd = this.mapDistance;
        let p = this.position;
        let c = this.system.center;
        return (p.x < c.x + vd && p.x > c.x - vd && p.y < c.y + vd && p.y > c.y - vd);
    }

    public isStarVisible = false;

    public show() {
        if (this.isStarVisible) return;
        this.isStarVisible = true;
        this.createStar();
    }

    public hide() {
        if (!this.isStarVisible) return;
        this.isStarVisible = false;
        this.removeAllPlanets();
        this._disposeStar();
    }

    public satellites: Planet[];

    public removeAllPlanets() {
        for (let i = 0; i < this.satellites.length; i++) {
            const planet = this.satellites[i];
            planet.hide();
            this.planetMap.storagePlanet(planet);
        }
        this.removeAllSatellites();
    }
}